import React from "react";
import { useSearchParams } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import SearchSelect from "@ui-components/Select/GenericSelect";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { DetailLink } from "../../Shared/DetailLink";
import { shapeExtenderPopData, type ExtenderPop } from "../ExtenderPopData";
import { fetchExtenderPop, fetchExtenderPopList } from "../requests/request";
import type { SiteLeg } from "../extenderPopCarrierAccessData";
import ExtenderPopCarrierAccess from "./ExtenderPopCarrierAccess";

/** What the page reads from the Extended PoP detail. */
interface SiteForAdmin {
  id: number;
  serviceId: string;
  name: string | null;
  cpes: { id: number; serviceId: string; name: string | null }[];
  carrierAccess: SiteLeg[];
}

const NO_SITES: ExtenderPop[] = [];

const siteLabel = (site: ExtenderPop): string =>
  [site.serviceId, site.name, site.customer?.name].filter(Boolean).join(" · ");

/**
 * Admin Center → Extended PoP Carrier Access (CTX-830). A Global Admin picks
 * a site and, on it, declares the Carrier Access (84-), links its free legs,
 * adds legs by hand, takes them out and captures each carrier's medium
 * (CTX-800). The Extended PoP detail shows the same section read-only.
 *
 * The site is kept in the URL (`?extenderPopId=`) so the page can be linked.
 */
const ExtenderPopCarrierAccessAdminPage: React.FC = () => {
  const api = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get("extenderPopId")) || null;

  const { data: sites = NO_SITES, error: listError } = useCachedFetch(
    tenantId ? `admin/extender-pops?tenantId=${tenantId}` : null,
    async () => {
      const { data } = await fetchExtenderPopList(api, tenantId);
      if (data?.success === false) {
        throw new Error(data.message || "Could not load the Extended PoPs");
      }
      return Array.isArray(data?.data) ? shapeExtenderPopData(data.data) : [];
    }
  );

  const {
    data: site,
    error: siteError,
    isLoading: isLoadingSite,
    reload,
  } = useCachedFetch<SiteForAdmin>(
    selectedId && tenantId
      ? `admin/extender-pops/${selectedId}?tenantId=${tenantId}`
      : null,
    async () => {
      const response = await fetchExtenderPop(api, selectedId as number, tenantId);
      if (!response.data?.success || !response.data.data) {
        throw new Error(response.data?.message || "Could not load the Extended PoP");
      }
      return response.data.data as SiteForAdmin;
    }
  );

  if (listError) return <ErrorFetchingItems text={listError} />;

  return (
    <div className="flex flex-col gap-4 px-4 py-4 overflow-y-auto h-full">
      <GenericCard
        headerContent="Extended PoP Carrier Access"
        isCollapsible={false}
        bodyContent={
          <div className="flex flex-col gap-3">
            <p className="text-sm text-semantic-text-secondary">
              {sites.length === 0
                ? "No Extended PoP for this selection."
                : "Declare the Carrier Access of an Extended PoP, change its legs and capture the type of each carrier."}
            </p>
            <div className="flex items-end gap-4">
              <SearchSelect
                key={selectedId ?? "none"}
                label="Extended PoP"
                placeholder="Select an Extended PoP"
                options={sites.map((option) => ({
                  value: String(option.id),
                  label: siteLabel(option),
                }))}
                defaultValue={selectedId ? String(selectedId) : undefined}
                onSelect={(value) => setSearchParams({ extenderPopId: value })}
              />
              {site && (
                <DetailLink
                  to={buildDetailPath("extenderPop", { extenderPopId: site.id })}
                  label={`Open ${site.serviceId}`}
                />
              )}
            </div>
          </div>
        }
      />

      {siteError && <ErrorFetchingItems text={siteError} />}
      {selectedId && isLoadingSite && (
        <GenericCard isLoading isCollapsible={false} bodyContent={null} />
      )}
      {site && (
        <ExtenderPopCarrierAccess
          extenderPopId={site.id}
          tenantId={tenantId}
          siteName={site.name}
          cpes={site.cpes}
          legs={site.carrierAccess}
          canManage
          onChanged={reload}
        />
      )}
    </div>
  );
};

export default ExtenderPopCarrierAccessAdminPage;
